import capitalize from 'lodash/capitalize';

import {
  WIDGET_FILTER_ENDPOINT,
  WIDGET_FILTER_VALUES_ENDPOINT,
  WIDGET_FILTER_SELECTED_ENDPOINT,
  SAVE_WIDGET_FILTER_VALUES_ENDPOINT,
} from 'constants/apis';
import {
  performGet,
  performPost,
  performDelete,
} from 'services/rest-service/rest-service';

import { Filter, FilterId, Namespace, Value } from './filters-definintions';

export const GET_FILTERS = 'GET_FILTERS';
export const GET_FILTER_VALUES = 'GET_FILTER_VALUES';
export const GET_FILTERS_SUCCESS = 'GET_FILTERS_SUCCESS';
export const GET_FILTER_VALUES_SUCCESS = 'GET_FILTER_VALUES_SUCCESS';
export const GET_PERSISTED_FILTER_VALUES_SUCCESS =
  'GET_PERSISTED_FILTER_VALUES_SUCCESS';
export const GET_FILTERS_ERROR = 'GET_FILTERS_ERROR';
export const TOGGLE_FILTER_ONE_VALUE = 'TOGGLE_FILTER_ONE_VALUE';
export const TOGGLE_FILTER_SEVERAL_VALUE = 'TOGGLE_FILTER_SEVERAL_VALUE';
export const REMOVE_ALL_SELECTED_VALUES = 'REMOVE_ALL_SELECTED_VALUES';
export const REMOVE_FILTER = 'REMOVE_FILTER';
export const REMOVE_FILTER_SUCCESS = 'REMOVE_FILTER_SUCCESS';
export const REMOVE_FILTER_ERROR = 'REMOVE_FILTER_ERROR';
export const GET_SELECTED_FILTERS = 'GET_SELECTED_FILTERS';
export const GET_SELECTED_FILTERS_SUCCESS = 'GET_SELECTED_FILTERS_SUCCESS';
export const GET_SELECTED_FILTERS_ERROR = 'GET_SELECTED_FILTERS_ERROR';
export const GET_PERSISTED_FILTERS = 'GET_PERSISTED_FILTER';
export const GET_PERSISTED_FILTERS_SUCCESS = 'GET_PERSISTED_FILTER_SUCCESS';
export const GET_PERSISTED_FILTERS_ERROR = 'GET_PERSISTED_FILTER_ERROR';
export const SAVE_FILTERS = 'SAVE_FILTERS';
export const SAVE_FILTERS_SUCCESS = 'SAVE_FILTERS_SUCCESS';
export const SAVE_FILTERS_ERROR = 'SAVE_FILTERS_ERROR';
export const SET_IS_USER_SAVING_FILTERS = 'SET_IS_USER_SAVING_FILTERS';

export const initialState = {
  loading: false,
  error: null,
  filters: [],
  filtersCount: 0,
  persistedFilters: [],
  isUserSavingFilters: false,
};

const findFilterIndex = (state, action) =>
  state.filters.findIndex((f: Filter) => f.id === action.payload.id);

const getNewValues = (values, savedValues) => {
  const newValues = [...values];
  savedValues.forEach(savedValue => {
    const valueToUpdate = newValues.find(val => val.value === savedValue.value);
    if (valueToUpdate) {
      valueToUpdate.selected = true;
    } else {
      newValues.push(savedValue);
    }
  });
  return newValues;
};

const buildSelectedFilters = (state, action) => {
  const savedFilters: Filter[] = action.payload.map(f => ({
    id: f.id,
    multivalued: f.multivalued,
    name: f.name,
    values: f.selected.map(v => ({
      key: capitalize(v),
      value: v,
      selected: true,
    })),
  }));
  return state.filters.map((filter: Filter) => {
    const savedFilter = savedFilters.find(saved => saved.id === filter.id);
    if (savedFilter) {
      const savedValues = savedFilter.values || [];
      const values = getNewValues(filter.values || [], savedValues);
      return {
        ...filter,
        values,
      };
    }
    return filter;
  });
};

export const widgetFiltersReducer = (
  state = { ...initialState },
  action: {
    type: string;
    payload: { filters: Filter; total: number; id: string | number | boolean };
  },
) => {
  switch (action.type) {
    case GET_FILTERS:
      return {
        ...state,
        loading: true,
        error: null,
      };
    case GET_FILTERS_SUCCESS:
      return {
        ...state,
        filters: action.payload.filters,
        filtersCount: action.payload.total,
        loading: false,
        error: null,
      };
    case GET_FILTERS_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false,
      };
    case GET_FILTER_VALUES: {
      const filterIndex = findFilterIndex(state, action);
      return {
        ...state,
        filters: [
          ...state.filters.slice(0, filterIndex),
          { ...action.payload, isLoadingValues: true },
          ...state.filters.slice(filterIndex + 1),
        ],
        loading: false,
        error: null,
      };
    }
    case TOGGLE_FILTER_ONE_VALUE:
    case TOGGLE_FILTER_SEVERAL_VALUE:
    case REMOVE_ALL_SELECTED_VALUES:
    case GET_FILTER_VALUES_SUCCESS: {
      const filterIndex = findFilterIndex(state, action);
      return {
        ...state,
        filters: [
          ...state.filters.slice(0, filterIndex),
          action.payload,
          ...state.filters.slice(filterIndex + 1),
        ],
        loading: false,
        error: null,
      };
    }
    case GET_SELECTED_FILTERS:
      return {
        ...state,
        loading: true,
        error: null,
      };
    case GET_SELECTED_FILTERS_SUCCESS: {
      const filters = buildSelectedFilters(state, action);
      return {
        ...state,
        filters,
        filtersCount: action.payload.total,
        loading: false,
        error: null,
      };
    }
    case GET_SELECTED_FILTERS_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false,
      };
    case GET_PERSISTED_FILTER_VALUES_SUCCESS:
      return {
        ...state,
        persistedFilters: action.payload,
      };
    case SET_IS_USER_SAVING_FILTERS:
      return {
        ...state,
        isUserSavingFilters: action.payload,
      };
    case SAVE_FILTERS:
      return {
        ...state,
        loading: true,
        error: null,
      };
    case SAVE_FILTERS_SUCCESS:
      return {
        ...state,
        loading: false,
        error: null,
        isUserSavingFilters: false,
      };
    case SAVE_FILTERS_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false,
      };
    default:
      return state;
  }
};

export const getFiltersActionCreator = (
  nameSpace: Namespace,
  callback: () => void,
) => async (dispatch: (arg0: { type: string; payload?: any }) => void) => {
  dispatch({ type: GET_FILTERS });
  try {
    const response = await performGet(WIDGET_FILTER_ENDPOINT, {
      nameSpace: nameSpace,
    });
    dispatch({ payload: response.data, type: GET_FILTERS_SUCCESS });
    callback();
  } catch (e) {
    dispatch({ payload: e, type: GET_FILTERS_ERROR });
  }
};

const setValuesToFilter = (filter: Filter, values: Value) => {
  return {
    ...filter,
    values: values,
  };
};

const hasFilterValues = (filter: Filter | any) => {
  return filter.values?.length > 0;
};

export const setUserIsSavingFiltersActionCreator = isSavingFilter => async dispatch => {
  dispatch({ payload: isSavingFilter, type: SET_IS_USER_SAVING_FILTERS });
};

export const getFilterValuesActionCreator = (
  filterId: FilterId | any,
) => async (dispatch, getState) => {
  const filter = getState().widgetFilters.filters.find(
    (f: Filter | any) => f.id === filterId,
  );
  if (!hasFilterValues(filter)) {
    dispatch({ payload: filter, type: GET_FILTER_VALUES });
    try {
      const response = await performGet(
        WIDGET_FILTER_VALUES_ENDPOINT.replace('{filterId}', filterId),
      );
      const updatedFilter = setValuesToFilter(filter, response.data);
      dispatch({ payload: updatedFilter, type: GET_FILTER_VALUES_SUCCESS });
    } catch (e) {
      dispatch({ payload: e, type: GET_FILTERS_ERROR });
    }
  }
};

const getFilter = (state, filterId: string) =>
  state.widgetFilters.filters.find((f: Filter) => f.id === filterId);

const toggleFilterOneValue = (filter: Filter | any, value: Value) => {
  const valueIndex = filter.values.findIndex(
    (v: Value) => v.value === value.value,
  );
  return {
    ...filter,
    values: [
      ...filter.values.slice(0, valueIndex),
      value,
      ...filter.values.slice(valueIndex + 1),
    ],
  };
};

export const setSelectedFilterOneValueActionCreator = (
  filterId: string,
  toggledFilterValue: Value,
) => async (dispatch, getState) => {
  const filter = getFilter(getState(), filterId);
  const filterWithToggledValue = toggleFilterOneValue(
    filter,
    toggledFilterValue,
  );
  dispatch({ payload: filterWithToggledValue, type: TOGGLE_FILTER_ONE_VALUE });
};

export const setSelectedFilterSeveralValuesActionCreator = (
  filterId: string,
  toggledFilterValues,
) => async (dispatch, getState) => {
  const filter = getFilter(getState(), filterId);
  const filterWithToggledValue = setValuesToFilter(filter, toggledFilterValues);
  dispatch({
    payload: filterWithToggledValue,
    type: TOGGLE_FILTER_SEVERAL_VALUE,
  });
};

export const removeAllSelectedFiltersActionCreator = (
  filterId: string,
) => async (dispatch, getState) => {
  const filter = getFilter(getState(), filterId);
  const updatedFilter = setValuesToFilter(
    filter,
    filter.values.map((v: Value) => {
      return {
        ...v,
        selected: false,
      };
    }),
  );
  dispatch({ payload: updatedFilter, type: REMOVE_ALL_SELECTED_VALUES });
};

const buildRequestFromIds = (filters: string[]) => {
  return filters.map(filterId =>
    performGet(WIDGET_FILTER_VALUES_ENDPOINT.replace('{filterId}', filterId)),
  );
};

export const getSelectedFiltersActionCreator = (
  searchId: string,
  widgetId: string,
) => async (
  dispatch: (arg0: { type: string; payload?: any }) => void,
  getState,
) => {
  dispatch({ type: GET_SELECTED_FILTERS });
  try {
    const response = await performGet(
      WIDGET_FILTER_SELECTED_ENDPOINT.replace('{searchId}', searchId),
      {
        context: `widget:${widgetId}`,
      },
    );
    const filterIds = response.data.map(f => f.id);
    const requests = buildRequestFromIds(filterIds);
    const values = await Promise.all(requests);
    const persistedFilters: any = [];
    values.forEach((value, i) => {
      const filter = getState().widgetFilters.filters.find(
        (f: Filter | any) => f.id === filterIds[i],
      );
      const updatedFilter: any = setValuesToFilter(filter, value.data);
      persistedFilters.push(updatedFilter);
      dispatch({ payload: updatedFilter, type: GET_FILTER_VALUES_SUCCESS });
    });
    dispatch({
      payload: persistedFilters,
      type: GET_PERSISTED_FILTER_VALUES_SUCCESS,
    });
    dispatch({ payload: response.data, type: GET_SELECTED_FILTERS_SUCCESS });
  } catch (e) {
    dispatch({ payload: e, type: GET_SELECTED_FILTERS_ERROR });
  }
};

export const getPersistedFiltersActionCreator = (
  searchId: number,
  widgetId: number,
) => async (
  dispatch: (arg0: { type: string; payload?: any }) => void,
  getState,
) => {
  try {
    dispatch({ payload: widgetId, type: GET_PERSISTED_FILTERS });

    const response = await performGet(
      WIDGET_FILTER_SELECTED_ENDPOINT.replace(
        '{searchId}',
        searchId.toString(),
      ),
      {
        context: `widget:${widgetId}`,
      },
    );

    dispatch({
      payload: { filters: response.data, widgetId: widgetId },
      type: GET_PERSISTED_FILTERS_SUCCESS,
    });
  } catch (e) {
    dispatch({
      payload: { ...e, widgetId: widgetId },
      type: GET_PERSISTED_FILTERS_ERROR,
    });
  }
};

export const saveSelectedFiltersActionCreator = (
  selectedFilters: Filter[],
  widgetId,
  searchId,
  filterType = 'MUST',
) => async (dispatch, getState) => {
  dispatch({ type: SAVE_FILTERS });

  const currentlySelectedFiltersIDs = selectedFilters.map(f => f.id);

  const filtersToDelete = getState().widgetFilters.persistedFilters.filter(
    f => !currentlySelectedFiltersIDs.includes(f.id),
  );

  if (filtersToDelete.length > 0) {
    if (selectedFilters.length > 0) {
      filtersToDelete.map(f => {
        performDelete(
          `${SAVE_WIDGET_FILTER_VALUES_ENDPOINT}?searchId=${searchId}&context=widget:${widgetId}&filterType=${f.id}`,
        );
      });
    } else {
      performDelete(
        `${SAVE_WIDGET_FILTER_VALUES_ENDPOINT}?searchId=${searchId}&context=widget:${widgetId}`,
      );
    }
  }

  if (selectedFilters.length > 0) {
    const params = {
      searchId,
      context: `widget:${widgetId}`,
      persistedFilterRequest: {
        filters: selectedFilters.map((f: Filter) => {
          return {
            filterId: f.id,
            filterType,
            selected: f.values?.map((v: Value) => v.value),
          };
        }),
        filterContext: [`widget:${widgetId}`],
      },
    };

    try {
      performPost(SAVE_WIDGET_FILTER_VALUES_ENDPOINT, params);
      dispatch({ type: SAVE_FILTERS_SUCCESS });
    } catch (e) {
      dispatch({ payload: e, type: SAVE_FILTERS_ERROR });
    }
  }
};

export default widgetFiltersReducer;
